/** Bottom-nav order — also the swipe order (left = next, right = previous). */
export const TAB_ORDER = ["now", "talk", "help", "you", "beacon"] as const;
export type SwipeTab = (typeof TAB_ORDER)[number];

/** Pure gesture → tab: a mostly-horizontal swipe of ≥ 60 px moves one tab along TAB_ORDER;
 *  null at the ends, for short/vertical gestures, or from a non-nav page (commons). */
export function swipeTarget(active: string | undefined, g: { dx: number; dy: number }): SwipeTab | null {
  if (Math.abs(g.dx) < 60 || Math.abs(g.dy) > 40 || Math.abs(g.dy) > Math.abs(g.dx) / 2) return null;
  const i = TAB_ORDER.indexOf(active as SwipeTab);
  if (i < 0) return null;
  const j = g.dx < 0 ? i + 1 : i - 1;
  return j >= 0 && j < TAB_ORDER.length ? TAB_ORDER[j] : null;
}

/** Elements that own horizontal gestures: a swipe starting inside one is not a tab swipe. */
const SWIPE_OWNERS = ".maplibregl-map, .now-map, .home-map, .report-sheet__picker, .detail-carousel__track, input[type=range]";

export interface ShellCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  simulation: boolean;
  body: HTMLElement;
  /** Active tab id. `commons` is a routable secondary page (#/commons), not a bottom-nav
   *  button — the nav tuple below stays crisis-focused (now/talk/help/you). */
  activeTab?: "now" | "help" | "you" | "talk" | "beacon" | "commons";
  helpUnread?: boolean;
}

/** App chrome: SIMULATION banner (when relay /info says so, ADR-13) + bottom tab bar. */
export function shell(ctx: ShellCtx): HTMLElement {
  const root = document.createElement("div");
  root.className = "app-shell";

  if (ctx.simulation) {
    const banner = document.createElement("div");
    banner.className = "sim-banner bx-microlabel";
    banner.setAttribute("role", "status");
    banner.setAttribute("data-sim-banner", "");
    banner.setAttribute("data-testid", "sim-indicator");
    banner.setAttribute("aria-label", ctx.t("banner.simulation"));
    banner.textContent = ctx.t("banner.simulation");
    root.append(banner);
  }

  const main = document.createElement("main");
  main.className = "app-main";
  main.append(ctx.body);
  root.append(main);

  // Swipe left/right between tabs (phone). Maps, pickers, carousels and sliders keep their
  // own horizontal gestures; the hash router does the actual navigation.
  let start: { x: number; y: number } | null = null;
  main.addEventListener("touchstart", (e) => {
    const target = e.target as Element | null;
    start = target?.closest?.(SWIPE_OWNERS) ? null : { x: e.touches[0]?.clientX ?? 0, y: e.touches[0]?.clientY ?? 0 };
  }, { passive: true });
  main.addEventListener("touchend", (e) => {
    if (!start) return;
    const end = e.changedTouches[0];
    const next = end ? swipeTarget(ctx.activeTab, { dx: end.clientX - start.x, dy: end.clientY - start.y }) : null;
    start = null;
    if (next) location.hash = `#/${next}`;
  }, { passive: true });

  const tabs = document.createElement("nav");
  tabs.className = "tab-bar";
  tabs.setAttribute("aria-label", "Tabs");
  // Wordmark — shown only on the wide (laptop) top-nav layout; CSS hides it on the
  // phone/tablet bottom-tab surface. Not a [data-tab], so tab wiring/tests are unaffected.
  const brand = document.createElement("a");
  brand.className = "tab-bar__brand";
  brand.href = "#/now";
  brand.setAttribute("aria-label", ctx.t("tab.now"));
  const mark = document.createElement("span");
  mark.className = "tab-bar__mark";
  mark.setAttribute("aria-hidden", "true");
  mark.textContent = "△";
  const word = document.createElement("span");
  word.className = "tab-bar__word bx-display";
  word.textContent = "Beacon";
  brand.append(mark, word);
  tabs.append(brand);
  for (const id of TAB_ORDER) {
    const a = document.createElement("a");
    a.className = `tab${ctx.activeTab === id ? " tab--active" : ""}`;
    a.href = `#/${id}`;
    a.setAttribute("data-tab", id);
    a.setAttribute("aria-label", ctx.t(`tab.${id}`));
    if (id === "beacon") { // icon-only: the Beacon mark; the label is for screen readers
      a.classList.add("tab--icon");
      a.textContent = "△";
      a.setAttribute("title", ctx.t(`tab.${id}`));
    } else {
      a.textContent = ctx.t(`tab.${id}`);
    }
    if (id === "help" && ctx.helpUnread) {
      const dot = document.createElement("span");
      dot.className = "tab__dot";
      dot.setAttribute("aria-hidden", "true");
      a.append(dot);
    }
    tabs.append(a);
  }
  root.append(tabs);
  return root;
}
